import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { WATCHLIST_PAGE_SIZE } from './watchlistPageSize';

interface WatchlistPaginationProps {
  page: number;
  onPageChange: (page: number) => void;
  hasNext: boolean;
  total: number;
  entityLabel: string;
}

/** "Showing 1-10 of 24 tracks" with Previous and Next. Hidden when one page holds every row. */
const WatchlistPaginationComponent: React.FC<WatchlistPaginationProps> = ({
  page,
  onPageChange,
  hasNext,
  total,
  entityLabel,
}) => {
  const intl = useIntl();
  const goBack = useCallback(() => onPageChange(page - 1), [onPageChange, page]);
  const goNext = useCallback(() => onPageChange(page + 1), [onPageChange, page]);

  if (total <= WATCHLIST_PAGE_SIZE) {
    return null;
  }

  const offset = (page - 1) * WATCHLIST_PAGE_SIZE;

  return (
    <Row className="mt-3">
      <Col className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          {intl.formatMessage(
            {
              id: 'app.sml.home.showingItems',
              defaultMessage: 'Showing {from}-{to} of {total} {entity}',
            },
            {
              from: offset + 1,
              to: Math.min(offset + WATCHLIST_PAGE_SIZE, total),
              total,
              entity: entityLabel,
            }
          )}
        </div>
        <div className="d-flex gap-2">
          <Button color="outline-primary" size="sm" disabled={page <= 1} onClick={goBack}>
            {intl.formatMessage({ id: 'app.sml.pagination.previous', defaultMessage: 'Previous' })}
          </Button>
          <Button color="outline-primary" size="sm" disabled={!hasNext} onClick={goNext}>
            {intl.formatMessage({ id: 'app.sml.pagination.next', defaultMessage: 'Next' })}
          </Button>
        </div>
      </Col>
    </Row>
  );
};

export const WatchlistPagination = TypedMemo(WatchlistPaginationComponent);
